import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/ui/Header";
import ResultsTabs from "../../../components/ui/ResultsTabs";
import PerformanceMetrics from "./components/PerformanceMetrics";
import QuestionAnalysis from "./components/QuestionAnalysis";
import RecommendedCourses from "./components/RecommendedCourses";
import FeedbackForm from "./components/FeedbackForm";
import Button from "../../../components/ui/Button";
import api from "../../../api/axiosInstance";

const InterviewResultsHub = () => {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("assessment");
  const [selectedType, setSelectedType] = useState("mockTest");

  const [mockTestData, setMockTestData] = useState(null);
  const [selectedAttempt, setSelectedAttempt] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // ✅ FETCH DATA
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const userStr = localStorage.getItem("user");
        const user = userStr ? JSON.parse(userStr) : null;

        if (!user?.email) {
          setError("User not found");
          return;
        }

        const userId = user.email;

        // FETCH OVERALL
        const res = await api.get(`/api/mock-test/overall/${userId}`);
        setMockTestData(res.data);

      } catch (err) {
        setError("Failed to load performance data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // ✅ SELECT ATTEMPT
  const handleSelectAttempt = async (testId) => {
    try {
      if (!testId) return;

      const res = await api.get(`/api/mock-test/${testId}`);
      setSelectedAttempt(res.data);

    } catch (err) {
      console.error(err);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleFeedbackSubmit = () => {
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const handleNewInterview = () => {
    navigate("/candidate/active-interview-session");
  };

  // ✅ SKILL CALCULATION
  const calculateSkills = (answers = []) => {
    const skills = {
      Aptitude: 0,
      Technical: 0,
      Coding: 0,
      Communication: 0,
      ProblemSolving: 0,
      CriticalThinking: 0,
    };

    answers.forEach((a) => {
      const score = a.score || 0;

      if (a.topic === "aptitude") skills.Aptitude += score;
      if (a.topic === "technical") skills.Technical += score;
      if (a.topic === "coding") skills.Coding += score;
      if (a.topic === "communication") skills.Communication += score;
      if (a.topic === "problem") skills.ProblemSolving += score;
      if (a.topic === "critical") skills.CriticalThinking += score;
    });

    return skills;
  };

  const getCategoryScores = (skills) => {
    // Calculate max possible score from the highest skill score
    const maxSkillScore = Math.max(skills.Aptitude, skills.Technical, skills.Coding, skills.Communication, skills.ProblemSolving, skills.CriticalThinking, 1);
    
    return [
      { id: 1, name: "Aptitude", score: Math.round((skills.Aptitude / maxSkillScore) * 100) },
      { id: 2, name: "Technical Skills", score: Math.round((skills.Technical / maxSkillScore) * 100) },
      { id: 3, name: "Coding", score: Math.round((skills.Coding / maxSkillScore) * 100) },
      { id: 4, name: "Communication", score: Math.round((skills.Communication / maxSkillScore) * 100) },
      { id: 5, name: "Problem Solving", score: Math.round((skills.ProblemSolving / maxSkillScore) * 100) },
      { id: 6, name: "Critical Thinking", score: Math.round((skills.CriticalThinking / maxSkillScore) * 100) },
    ];
  };

  const currentData = selectedAttempt || mockTestData;
  // Use backend skills directly, but fallback to frontend calculation for selected attempts
  const skills = selectedAttempt 
    ? calculateSkills(selectedAttempt?.answers || [])
    : (mockTestData?.skills || {
        Aptitude: 0,
        Technical: 0,
        Coding: 0,
        Communication: 0,
        ProblemSolving: 0,
        CriticalThinking: 0
      });

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-[60px]">
        {/* HEADER */}
        <div className="bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10 border-b">
          <div className="max-w-7xl mx-auto px-4 py-8 flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Performance Dashboard</h1>
              <p>{new Date().toLocaleDateString()}</p>
            </div>

            <Button onClick={handleNewInterview}>
              Start New Interview
            </Button>
          </div>
        </div>

        <ResultsTabs activeTab={activeTab} onTabChange={handleTabChange} />

        <div className="max-w-7xl mx-auto px-4 py-8">

          {/* TOGGLE */}
          <div className="flex gap-4 mb-6">
            <button
              className={`px-4 py-2 rounded ${
                selectedType === "mockTest"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => setSelectedType("mockTest")}
            >
              Mock Test
            </button>

            <button
              className={`px-4 py-2 rounded ${
                selectedType === "interview"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => setSelectedType("interview")}
            >
              Mock Interview
            </button>
          </div>

          {/* LOADING */}
          {loading && <div>Loading...</div>}

          {/* ERROR */}
          {error && <div className="text-red-500">{error}</div>}

          {/* ================== ASSESSMENT ================== */}
          {activeTab === "assessment" && !loading && !error && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              <div className="lg:col-span-2 space-y-6">

                <PerformanceMetrics
                  overallScore={
                    currentData?.finalScore ||
                    currentData?.averagePercentage ||
                    0
                  }
                  categoryScores={getCategoryScores(skills)}
                />

                {/* QUESTION ANALYSIS */}
                <QuestionAnalysis
                  questions={
                    selectedAttempt?.answers?.map((q, i) => ({
                      id: i,
                      question: q.question,
                      userAnswer: q.userAnswer,
                      isCorrect: q.score >= 5,
                      feedback: q.feedback,
                    })) || []
                  }
                />

                {/* PREVIOUS ATTEMPTS */}
                <div className="bg-card p-4 border rounded">
                  <h3 className="font-semibold mb-3">
                    Previous Attempts
                  </h3>

                  {mockTestData?.attempts?.map((test, index) => (
                    <div
                      key={test._id || index}
                      className={`p-3 border rounded mb-2 cursor-pointer ${
                        selectedAttempt?._id === test._id
                          ? "bg-blue-100"
                          : "hover:bg-gray-100"
                      }`}
                      onClick={() =>
                        test._id && handleSelectAttempt(test._id)
                      }
                    >
                      <div className="flex justify-between">
                        <span>
                          {new Date(test.date).toLocaleDateString()}
                        </span>
                        <span>{test.score}%</span>
                      </div>
                    </div>
                  ))}
                </div>

              </div>

              <div>
                <RecommendedCourses courses={[]} />
              </div>

            </div>
          )}

          {/* ================== MENTOR ================== */}
          {activeTab === "mentor" && (
            <div className="max-w-3xl mx-auto p-6 border rounded">
              <h2 className="text-xl font-semibold mb-4">
                Mentor Advice
              </h2>

              <ul className="list-disc ml-6 space-y-2">
                <li>Improve problem solving approach</li>
                <li>Work on communication clarity</li>
                <li>Strengthen technical fundamentals</li>
              </ul>
            </div>
          )}

          {/* ================== FEEDBACK ================== */}
          {activeTab === "feedback" && (
            <div className="max-w-2xl mx-auto">
              {showSuccessMessage && (
                <div className="text-green-600 mb-4">
                  Feedback submitted successfully!
                </div>
              )}

              <FeedbackForm onSubmit={handleFeedbackSubmit} />
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default InterviewResultsHub;